import { dia, g } from '@clientio/jointjs'
import { DirectedGraph } from '@joint/layout-directed-graph'
import { NodeFactory } from '../../core/workflow'
import { DiagramNode, NodeAttrs } from './nodes'
import { NodeLink } from './links'
import { GridSize } from './plugins'

/**
 * Automatically layouts the workflow by arranging nodes and links.
 * @param {WorkflowState} workflowState - The current state of the workflow containing nodes and links.
 */
export const autoLayoutWorkflow = workflowState => {
  // Early return if no nodes exist
  if (!workflowState?.nodes || Object.keys(workflowState.nodes).length === 0) {
    return
  }

  const graph = new dia.Graph()
  const cells = []

  // temporary create new nodes and links
  Object.values(workflowState.nodes).forEach(node => {
    const data = NodeFactory.createCanvasNode(node)
    const diagramNode = DiagramNode.create(data)
    cells.push(diagramNode)
  })

  Object.values(workflowState.links).forEach(link => {
    const diagramLink = NodeLink.create(link.source, link.target, link)
    cells.push(diagramLink)
  })
  graph.resetCells(cells)

  // layout temporary nodes and links
  DirectedGraph.layout(graph, {
    rankDir: 'LR',
    ranker: 'longest-path',
    align: 'DL',
    rankSep: 75,
    nodeSep: 75,
    edgeSep: 100
  })

  // update position for actual nodes and links in state
  cells.forEach(cell => {
    if (cell.isElement()) {
      const node = workflowState.nodes[cell.id]
      node.ui.position = cell.position().snapToGrid(GridSize).toJSON()
    }
  })
}

/**
 * Finds the nearest empty position on the canvas, expanding the search radius if necessary.
 * @param {DiagramPosition} startPosition
 * @param {NodesType} nodes
 * @returns {DiagramPosition} The nearest empty position.
 */
export const findNearestEmptyPosition = (startPosition, nodes) => {
  const result = findEmptySpace(startPosition, nodes, 400)
  if (result) {
    return result // return empty position when find empty space in 400 radius circle
  }
  // try to find empty space in 800 radius circle, when doesn't exist return start position
  return findEmptySpace(startPosition, nodes, 800) || startPosition
}

/**
 * Finds the nearest empty position on the canvas for placing a new node.
 * @param {DiagramPosition} startPosition - The starting X and Y coordinates.
 * @param {NodesType} nodes
 * @param {number} [maxRadius=400] - The maximum radius to search for an empty position.
 * @returns {DiagramPosition|undefined} The nearest empty position.
 */
export const findEmptySpace = (startPosition, nodes, maxRadius = 300) => {
  // Defensive guards for invalid inputs
  if (!startPosition || typeof startPosition.x !== 'number' || typeof startPosition.y !== 'number') {
    // eslint-disable-next-line no-console
    console.warn('Invalid startPosition provided to findEmptySpace:', startPosition)
    return { x: 0, y: 0 }
  }

  if (!nodes) {
    nodes = {}
  }

  const width = NodeAttrs.nodeSize.width
  const height = NodeAttrs.nodeSize.height

  // Filter out nodes without valid ui.position before processing
  const validNodes = Object.values(nodes).filter(
    node => node?.ui?.position && typeof node.ui.position.x === 'number' && typeof node.ui.position.y === 'number'
  )

  const existingRects = validNodes.map(n => n.ui.position).map(pos => new g.Rect(pos.x, pos.y, width, height))

  const sourceRect = new g.Rect(startPosition.x, startPosition.y, width, height)

  // Check if initial position is already free
  const isInitialFree = !existingRects.some(rect => rect.intersect(sourceRect))

  if (isInitialFree) {
    return startPosition
  }

  const center = { x: startPosition.x + 1.5 * width, y: startPosition.y } // inflate width to have space around shape

  for (let radius = 0; radius <= maxRadius; radius += GridSize) {
    for (let angle = 0; angle < 360; angle += 15) {
      const rad = (angle * Math.PI) / 180
      const candidateCenter = new g.Point(center.x + radius * Math.cos(rad), center.y + radius * Math.sin(rad))

      const candidateRect = new g.Rect(candidateCenter.x, candidateCenter.y, width, height).inflate(2 * GridSize)

      const isOccupied = existingRects.some(rect => rect.intersect(candidateRect))

      if (!isOccupied) {
        return candidateCenter.snapToGrid(GridSize).toJSON()
      }
    }
  }
}
